import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, Check, X, Tag } from 'lucide-react';

const MultiSelectSearchable = ({
  label,
  value = [],
  onChange,
  options = [],
  placeholder = 'Select options',
  searchPlaceholder = 'Search...',
  error,
  required = false,
  disabled = false,
  className = '',
  optionLabel = 'label',
  optionValue = 'value',
  optionDescription = 'description',
  maxHeight = '200px'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = options.filter(option => {
    const label = typeof option === 'object' ? option[optionLabel] : option;
    const description = typeof option === 'object' ? option[optionDescription] : '';
    const searchString = `${label} ${description}`.toLowerCase();
    return searchString.includes(searchTerm.toLowerCase());
  });

  // Get display value
  const getDisplayValue = () => {
    if (!value || value.length === 0) return placeholder;
    
    const selectedOptions = options.filter(option => {
      const optValue = typeof option === 'object' ? option[optionValue] : option;
      return value.includes(optValue);
    });

    if (selectedOptions.length === 0) return placeholder;
    
    if (selectedOptions.length === 1) {
      const option = selectedOptions[0];
      return typeof option === 'object' ? option[optionLabel] : option;
    }
    
    return `${selectedOptions.length} selected`;
  };

  const handleToggle = (option) => {
    const optValue = typeof option === 'object' ? option[optionValue] : option;
    
    let newValue;
    if (value.includes(optValue)) {
      newValue = value.filter(v => v !== optValue);
    } else {
      newValue = [...value, optValue];
    }
    
    onChange({ target: { value: newValue } });
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ target: { value: [] } });
  };

  const isSelected = (option) => {
    const optValue = typeof option === 'object' ? option[optionValue] : option;
    return value.includes(optValue);
  };

  return (
    <div className={`mb-4 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`
            w-full px-3 py-2.5 text-left bg-white border rounded-lg
            flex items-center justify-between
            transition-all duration-200
            ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'hover:border-gray-400 hover:shadow-sm cursor-pointer'}
            ${error ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'}
            ${isOpen ? 'ring-2 ring-primary-500 border-primary-500 shadow-sm' : ''}
          `}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className={`p-1 rounded ${value.length > 0 ? 'bg-primary-50' : 'bg-gray-100'}`}>
              <Tag className={`w-4 h-4 ${value.length > 0 ? 'text-primary-600' : 'text-gray-400'} flex-shrink-0`} />
            </div>
            <span className={`block truncate text-sm ${value.length === 0 ? 'text-gray-400' : 'text-gray-900 font-medium'}`}>
              {getDisplayValue()}
            </span>
          </div>
          
          <div className="flex items-center gap-1 ml-2">
            {value.length > 0 && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 hover:bg-gray-100 rounded-md transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
            <ChevronDown 
              className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>
        </button>

        {isOpen && (
          <div className="absolute z-10 mt-2 w-full bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
            <div className="p-3 border-b border-gray-200 bg-gray-50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                />
              </div>
            </div>

            <div className={`overflow-y-auto`} style={{ maxHeight }}>
              {filteredOptions.length > 0 ? (
                <div className="py-1">
                  {filteredOptions.map((option, index) => {
                    const optLabel = typeof option === 'object' ? option[optionLabel] : option;
                    const optDesc = typeof option === 'object' ? option[optionDescription] : null;
                    const selected = isSelected(option);

                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleToggle(option)}
                        className={`
                          w-full px-4 py-2.5 text-left flex items-center gap-3
                          transition-all duration-150
                          ${selected
                            ? 'bg-primary-50 text-primary-900 hover:bg-primary-100 border-l-4 border-primary-600'
                            : 'text-gray-900 hover:bg-gray-50 border-l-4 border-transparent'
                          }
                        `}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => {}}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm ${selected ? 'font-semibold' : 'font-medium'}`}>{optLabel}</div>
                          {optDesc && (
                            <div className="text-xs text-gray-500 mt-0.5">{optDesc}</div>
                          )}
                        </div>
                        {selected && (
                          <Check className="w-4 h-4 text-primary-600 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="px-4 py-8 text-center text-gray-500 text-sm">
                  <Tag className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p className="font-medium">No options found</p>
                  <p className="text-xs mt-1">Try a different search term</p>
                </div>
              )}
            </div>

            {value.length > 0 && (
              <div className="px-4 py-2.5 bg-primary-50 border-t border-primary-100 flex items-center justify-between">
                <span className="text-xs font-semibold text-primary-900">
                  {value.length} item{value.length !== 1 ? 's' : ''} selected
                </span>
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-xs font-medium text-primary-600 hover:text-primary-700 underline"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="mt-2 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default MultiSelectSearchable;